
import { Component, Vue } from 'vue-property-decorator';
import { SearchTexts } from '@/types';
import { errorHandler } from '@/utils';
import {
  getMcVersions, createMcVersion, updateMcVersion, deleteMcVersion,
  getDescriptions, createDescription, updateDescription, deleteDescription,
  getVersionDescriptions, createVersionDescription, updateVersionDescription, deleteVersionDescription
} from '@/api/services/search-texts';

import SearchComponent from '@/components/SearchComponent.vue';
import InputComponent from '@/components/InputComponent.vue';
import TextareaComponent from '@/components/TextareaComponent.vue';
import DropdownComponent from '@/components/DropdownComponent.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import SpinnerComponent from '@/components/SpinnerComponent.vue';

@Component({
  metaInfo: { title: 'Поисковые тексты' },
  components: {
    SearchComponent, InputComponent, TextareaComponent,
    DropdownComponent, ButtonComponent, ModalComponent, SpinnerComponent
  }
})
export default class SearchTextsPage extends Vue {
  isLoading: boolean = true;
  activeTab: string = 'mcVersions';

  // --- Data ---
  mcVersions: SearchTexts.McVersion[] = [];
  descriptions: SearchTexts.DescriptionHtml[] = [];
  versionDescriptions: SearchTexts.VersionDescriptionItem[] = [];

  // --- Search queries ---
  mcvQuery: string = '';
  descQuery: string = '';
  vdQuery: string = '';

  // --- McVersion state ---
  selectedMcv: SearchTexts.McVersionForm = new SearchTexts.McVersionForm();
  isMcvAddModalShown: boolean = false;
  isMcvEditModalShown: boolean = false;
  isMcvDeleteModalShown: boolean = false;

  // --- DescriptionHtml state ---
  selectedDesc: SearchTexts.DescriptionHtmlForm = new SearchTexts.DescriptionHtmlForm();
  isDescAddModalShown: boolean = false;
  isDescEditModalShown: boolean = false;
  isDescDeleteModalShown: boolean = false;

  // --- VersionDescription state ---
  selectedVd: SearchTexts.VersionDescriptionForm = new SearchTexts.VersionDescriptionForm();
  selectedVdVersion: any = null;
  selectedVdType: any = null;
  selectedVdDescription: any = null;
  isVdAddModalShown: boolean = false;
  isVdEditModalShown: boolean = false;
  isVdDeleteModalShown: boolean = false;

  // --- Type options for dropdown ---
  get typeOptions() {
    return Object.values(SearchTexts.VersionTypeHtml).map(val => ({
      name: val,
      label: SearchTexts.VersionTypeHtmlLabels[val] || val
    }));
  }

  // --- Computed filters ---
  get filteredMcVersions(): SearchTexts.McVersion[] {
    if (!this.mcVersions) return [];
    if (!this.mcvQuery) return this.mcVersions;
    const q = this.mcvQuery.toLowerCase();
    return this.mcVersions.filter(it => it.version.toLowerCase().includes(q));
  }

  get filteredDescriptions(): SearchTexts.DescriptionHtml[] {
    if (!this.descriptions) return [];
    if (!this.descQuery) return this.descriptions;
    const q = this.descQuery.toLowerCase();
    return this.descriptions.filter(it =>
      it.title.toLowerCase().includes(q) || it.description.toLowerCase().includes(q)
    );
  }

  get filteredVersionDescriptions(): SearchTexts.VersionDescriptionItem[] {
    if (!this.versionDescriptions) return [];
    if (!this.vdQuery) return this.versionDescriptions;
    const q = this.vdQuery.toLowerCase();
    return this.versionDescriptions.filter(it =>
      (it.version_display && it.version_display.toLowerCase().includes(q)) ||
      (it.types && it.types.toLowerCase().includes(q)) ||
      (it.description_display && it.description_display.toLowerCase().includes(q)) ||
      (this.getTypeLabel(it.types).toLowerCase().includes(q))
    );
  }

  // --- Helpers ---
  getTypeLabel(type: string): string {
    return SearchTexts.VersionTypeHtmlLabels[type] || type;
  }

  truncate(str: string, len: number): string {
    if (!str) return '';
    return str.length > len ? str.substring(0, len) + '...' : str;
  }

  // ==================== McVersion CRUD ====================

  openMcvAddModal() {
    this.selectedMcv = new SearchTexts.McVersionForm();
    this.$nextTick(() => { this.isMcvAddModalShown = true; });
  }

  openMcvEditModal(item: SearchTexts.McVersion) {
    this.selectedMcv = new SearchTexts.McVersionForm(item);
    this.$nextTick(() => { this.isMcvEditModalShown = true; });
  }

  openMcvDeleteModal(item: SearchTexts.McVersion) {
    this.selectedMcv = new SearchTexts.McVersionForm(item);
    this.$nextTick(() => { this.isMcvDeleteModalShown = true; });
  }

  closeMcvModals() {
    this.isMcvAddModalShown = false;
    this.isMcvEditModalShown = false;
    this.isMcvDeleteModalShown = false;
    this.$nextTick(() => { this.selectedMcv = new SearchTexts.McVersionForm(); });
  }

  async addMcVersion() {
    this.selectedMcv.isLoading = true;
    try {
      await createMcVersion(this.selectedMcv.version.value);
      await this.refreshData();
      this.closeMcvModals();
    } catch (error) {
      this.handleError(error, this.selectedMcv);
    } finally {
      this.selectedMcv.isLoading = false;
    }
  }

  async editMcVersion() {
    this.selectedMcv.isLoading = true;
    try {
      await updateMcVersion(this.selectedMcv.id, this.selectedMcv.version.value);
      await this.refreshData();
      this.closeMcvModals();
    } catch (error) {
      this.handleError(error, this.selectedMcv);
    } finally {
      this.selectedMcv.isLoading = false;
    }
  }

  async removeMcVersion() {
    this.selectedMcv.isLoading = true;
    try {
      await deleteMcVersion(this.selectedMcv.id);
      await this.refreshData();
      this.closeMcvModals();
    } catch (error) {
      this.handleError(error, this.selectedMcv);
    } finally {
      this.selectedMcv.isLoading = false;
    }
  }

  // ==================== DescriptionHtml CRUD ====================

  openDescAddModal() {
    this.selectedDesc = new SearchTexts.DescriptionHtmlForm();
    this.$nextTick(() => { this.isDescAddModalShown = true; });
  }

  openDescEditModal(item: SearchTexts.DescriptionHtml) {
    this.selectedDesc = new SearchTexts.DescriptionHtmlForm(item);
    this.$nextTick(() => { this.isDescEditModalShown = true; });
  }

  openDescDeleteModal(item: SearchTexts.DescriptionHtml) {
    this.selectedDesc = new SearchTexts.DescriptionHtmlForm(item);
    this.$nextTick(() => { this.isDescDeleteModalShown = true; });
  }

  closeDescModals() {
    this.isDescAddModalShown = false;
    this.isDescEditModalShown = false;
    this.isDescDeleteModalShown = false;
    this.$nextTick(() => { this.selectedDesc = new SearchTexts.DescriptionHtmlForm(); });
  }

  async addDescription() {
    this.selectedDesc.isLoading = true;
    try {
      await createDescription({
        title: this.selectedDesc.title.value,
        description: this.selectedDesc.description.value,
        t_header: this.selectedDesc.t_header.value,
        t_text: this.selectedDesc.t_text.value,
        b_header: this.selectedDesc.b_header.value,
        b_text: this.selectedDesc.b_text.value,
      });
      await this.refreshData();
      this.closeDescModals();
    } catch (error) {
      this.handleError(error, this.selectedDesc);
    } finally {
      this.selectedDesc.isLoading = false;
    }
  }

  async editDescription() {
    this.selectedDesc.isLoading = true;
    try {
      await updateDescription(this.selectedDesc.id, {
        title: this.selectedDesc.title.value,
        description: this.selectedDesc.description.value,
        t_header: this.selectedDesc.t_header.value,
        t_text: this.selectedDesc.t_text.value,
        b_header: this.selectedDesc.b_header.value,
        b_text: this.selectedDesc.b_text.value,
      });
      await this.refreshData();
      this.closeDescModals();
    } catch (error) {
      this.handleError(error, this.selectedDesc);
    } finally {
      this.selectedDesc.isLoading = false;
    }
  }

  async removeDescription() {
    this.selectedDesc.isLoading = true;
    try {
      await deleteDescription(this.selectedDesc.id);
      await this.refreshData();
      this.closeDescModals();
    } catch (error) {
      this.handleError(error, this.selectedDesc);
    } finally {
      this.selectedDesc.isLoading = false;
    }
  }

  // ==================== VersionDescription CRUD ====================

  openVdAddModal() {
    this.selectedVd = new SearchTexts.VersionDescriptionForm();
    this.selectedVdVersion = null;
    this.selectedVdType = null;
    this.selectedVdDescription = null;
    this.$nextTick(() => { this.isVdAddModalShown = true; });
  }

  openVdEditModal(item: SearchTexts.VersionDescriptionItem) {
    this.selectedVd = new SearchTexts.VersionDescriptionForm(item);
    this.selectedVdVersion = this.mcVersions.find(v => v.id === item.version) || null;
    this.selectedVdType = this.typeOptions.find(t => t.name === item.types) || null;
    this.selectedVdDescription = this.descriptions.find(d => d.id === item.description) || null;
    this.$nextTick(() => { this.isVdEditModalShown = true; });
  }

  openVdDeleteModal(item: SearchTexts.VersionDescriptionItem) {
    this.selectedVd = new SearchTexts.VersionDescriptionForm(item);
    this.selectedVdVersion = this.mcVersions.find(v => v.id === item.version) || null;
    this.selectedVdType = this.typeOptions.find(t => t.name === item.types) || null;
    this.selectedVdDescription = this.descriptions.find(d => d.id === item.description) || null;
    this.$nextTick(() => { this.isVdDeleteModalShown = true; });
  }

  closeVdModals() {
    this.isVdAddModalShown = false;
    this.isVdEditModalShown = false;
    this.isVdDeleteModalShown = false;
    this.$nextTick(() => {
      this.selectedVd = new SearchTexts.VersionDescriptionForm();
      this.selectedVdVersion = null;
      this.selectedVdType = null;
      this.selectedVdDescription = null;
    });
  }

  async addVersionDescription() {
    if (!this.selectedVdVersion || !this.selectedVdType || !this.selectedVdDescription) {
      this.selectedVd.error = 'Заполните все поля';
      return;
    }
    this.selectedVd.isLoading = true;
    try {
      await createVersionDescription(
        this.selectedVdVersion.id,
        this.selectedVdType.name,
        this.selectedVdDescription.id
      );
      await this.refreshData();
      this.closeVdModals();
    } catch (error) {
      this.handleError(error, this.selectedVd);
    } finally {
      this.selectedVd.isLoading = false;
    }
  }

  async editVersionDescription() {
    if (!this.selectedVdVersion || !this.selectedVdType || !this.selectedVdDescription) {
      this.selectedVd.error = 'Заполните все поля';
      return;
    }
    this.selectedVd.isLoading = true;
    try {
      await updateVersionDescription(
        this.selectedVd.id,
        this.selectedVdVersion.id,
        this.selectedVdType.name,
        this.selectedVdDescription.id
      );
      await this.refreshData();
      this.closeVdModals();
    } catch (error) {
      this.handleError(error, this.selectedVd);
    } finally {
      this.selectedVd.isLoading = false;
    }
  }

  async removeVersionDescription() {
    this.selectedVd.isLoading = true;
    try {
      await deleteVersionDescription(this.selectedVd.id);
      await this.refreshData();
      this.closeVdModals();
    } catch (error) {
      this.handleError(error, this.selectedVd);
    } finally {
      this.selectedVd.isLoading = false;
    }
  }

  // ==================== Common ====================

  handleError(error: any, form: any) {
    const errResponse = error && error.data ? error.data : {};
    Object.keys(errResponse).forEach(it => {
      if (form[it] && form[it].error !== undefined) {
        form[it].error = errorHandler(it, errResponse[it][0]);
      } else {
        form.error = errorHandler(it, errResponse[it][0]);
      }
    });
  }

  async refreshData() {
    try {
      const [mcvRes, descRes, vdRes] = await Promise.all([
        getMcVersions(),
        getDescriptions(),
        getVersionDescriptions()
      ]);
      this.mcVersions = mcvRes.data;
      this.descriptions = descRes.data;
      this.versionDescriptions = vdRes.data;
    } catch (error) {
      console.error(error);
    }
  }

  async mounted() {
    try {
      await this.refreshData();
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }
}
